import {
  AfterViewInit,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ComponentRef
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';

import {TextFilterComponent} from '../filter-controls/text-filter/text-filter.component';
import {NumberFilterComponent} from '../filter-controls/number-filter/number-filter.component';
import {DateFilterComponent} from '../filter-controls/date-filter/date-filter.component';
import {DaterangeFilterComponent} from '../filter-controls/daterange-filter/daterange-filter.component';
import {TimeFilterComponent} from '../filter-controls/time-filter/time-filter.component';
import {EnumFilterComponent} from '../filter-controls/enum-filter/enum-filter.component';
import {BooleanFilterComponent} from '../filter-controls/boolean-filter/boolean-filter.component';
import { FilterBuilderService } from '../../services/filter-builder.service';
import { FilterControlParams } from '../../interfaces/filter-control-params';

enum InputTypes {
  TEXT = 'string',
  NUMBER = 'number',
  DATE = 'date',
  DATE_RANGE = 'daterange',
  TIME = 'time',
  ENUM = 'enum',
  BOOL = 'bool'
}

const InputTypeColSizes = {
  [InputTypes.TEXT]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.NUMBER]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.DATE]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.DATE_RANGE]: 'col-md-8 col-lg-6 col-xl-4',
  [InputTypes.TIME]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.ENUM]: 'col-md-4 col-lg-3 col-xl-2',
  [InputTypes.BOOL]: 'col-md-4 col-lg-3 col-xl-2'
};

interface SearchByParams {
  [key: string]: InputTypes;
}


@Component({
  selector: 'bm-filter-dashboard',
  templateUrl: './filter-dashboard.component.html',
  styleUrls: ['./filter-dashboard.component.scss']
})
export class FilterDashboardComponent implements OnInit, AfterViewInit {

  private _params: SearchByParams;

  @Input() set params(params) {
    if (JSON.stringify(params) !== JSON.stringify(this._params)) {
      this._params = params;
      this.initFormFields();
      this.fillFormFieldValues();
      this.renderFilters();
    } else {
      this.fillFormFieldValues();
    }
  }

  get params() {
    return this._params;
  }

  @Output() search = new EventEmitter();
  @Input() mutateQueryString = true;

  @ViewChild('filterOutlet', {static: false, read: ViewContainerRef})
  filterOutlet: ViewContainerRef;

  filters: FilterControlParams[] = [];

  readonly formFieldComponents = {
    [InputTypes.TEXT]: TextFilterComponent,
    [InputTypes.NUMBER]: NumberFilterComponent,
    [InputTypes.DATE]: DateFilterComponent,
    [InputTypes.TIME]: TimeFilterComponent,
    [InputTypes.ENUM]: EnumFilterComponent,
    [InputTypes.DATE_RANGE]: DaterangeFilterComponent,
    [InputTypes.BOOL]: BooleanFilterComponent
  };

  constructor(
    private factoryResolver: ComponentFactoryResolver,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private filterBuilder: FilterBuilderService,
  ) {
  }

  public static getFormLabel(name: string) {
    const label = name.replace(/_/g, ' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  ngOnInit() {
    this.initFormFields();
    this.fillFormFieldValues();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.renderFilters();
    });
  }

  initFormFields() {
    if (!this.params) {
      return;
    }

    this.filters = Object.entries(this.params).map(entry => this.filterBuilder.createFilterControl(entry));

  }

  fillFormFieldValues() {
    const queryParams = this.activatedRoute.snapshot.queryParams;
    const formFieldsWithValues = [];

    Object.entries(queryParams).forEach(entry => {
      this.filters.forEach(filter => {
        const touched = this.filterBuilder.setFilterControlValue(entry, filter);
        if (touched) {
          formFieldsWithValues.push(filter);
        }
      });
    });

    this.filters.forEach(formField => {
      if (!formFieldsWithValues.includes(formField) && formField.type !== 'daterange') {
        formField.value = null;
      }
    });
  }

  renderFilters() {
    this.filterOutlet.clear();
    this.filters.forEach(filter => {
      this.renderFilterControl(filter);
    });
  }

  renderFilterControl(filterControl: FilterControlParams) {
    const factory = this.factoryResolver.resolveComponentFactory(filterControl.component);
    const componentRef: ComponentRef<any> = this.filterOutlet.createComponent(factory);

    componentRef.instance.className = InputTypeColSizes[filterControl.type];
    componentRef.instance.control = filterControl;
  }

  performSearch() {
    const searchParams = {};
    this.filters.forEach(formField => {
      if (formField.value === null) {
        return;
      }
      if (formField.type === InputTypes.DATE_RANGE) {
        searchParams[`start_${formField.name}`] = formField.value.start;
        searchParams[`end_${formField.name}`] = formField.value.end;
      } else {
        searchParams[formField.name] = formField.value;
      }
    });

    Object.keys(searchParams).forEach(key => {
      if (searchParams[key] === null || !Array.isArray(searchParams[key])) {
        return;

      } else {
        searchParams[key] = searchParams[key].map(params => {
          const targetKey = Object.keys(params).find(k => k.startsWith('_'));

          return {
            name: params.name,
            [targetKey.slice(1)]: params[targetKey],
          };

        });
      }
    });

    this.search.emit(searchParams);

    const queryParams = this.filterBuilder.mapSearchByFields(searchParams, {});

    if (this.mutateQueryString) {
      this.router.navigate(
        [],
        {
          relativeTo: this.activatedRoute,
          queryParams,
          queryParamsHandling: 'merge'
        }
      );
    }
  }

  clearSearchForm() {
    this.filters.forEach(item => {
      switch (item.type) {
        case InputTypes.BOOL:
          item.value = null;
          item.compactValue = 'all';
          break;
        default:
          item.value = null;
          item.compactValue = null;
      }
    });
  }

}
